import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const SOURCE_LABEL = {
  ui_field: "Field grid", ui_ops: "Operations", excel_import: "Excel import",
  co_sync: "Change order", resubmittal: "Resubmittal", restore: "Restore",
  reconcile: "Reconciled", manual: "Manual",
};

/**
 * FT-3a §3 — history from the grid. Newest first, grid Saves alongside the versions. Pick a
 * version and the grid shows it read-only with the differences highlighted; nothing is ever
 * overwritten, and a restore is itself a new revision.
 */
export default function FrsHistoryDrawer({ projectId, viewing, onView, onClose, onRestored, inline = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/frs-grid/history", { params: { project_id: projectId } })
    .then((r) => setData(r.data)).catch(() => setData({ rows: [] }));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const restore = async (row) => {
    const label = `v${row.version_no}`;
    if (!window.confirm(`Restore the FRS to ${label}?\n\nThis writes a NEW revision — it never `
      + `deletes history, and it never rolls back installed progress or dispatched work.`)) return;
    setBusy(row.id);
    try {
      const { data: res } = await api.post("/frs-grid/restore", {
        project_id: projectId, version_id: row.id, note: `Restored ${label} from the grid`,
      });
      toast.success(`Restored ${label}`, { description: res.summary });
      await onRestored?.(res);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Could not restore that version");
    } finally {
      setBusy("");
    }
  };

  const body = (
    <>
        {!data ? (
          <div className="p-6 text-center text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : !data.rows.length ? (
          <div className="p-6 text-center text-sm text-gray-500" data-testid="frs-history-empty">
            No recorded changes yet.
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {data.rows.map((row) => {
              const isVersion = row.kind === "version";
              const active = viewing?.id === row.id;
              return (
                <li key={`${row.kind}-${row.id}`}
                    data-testid={`frs-history-row-${row.id}`}
                    className={`px-3 py-2 text-xs ${active ? "bg-blue-50" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {isVersion ? `v${row.version_no}` : "Grid save"}
                    </span>
                    <span className="px-1 rounded bg-gray-100 text-gray-600 text-[10px]">
                      {SOURCE_LABEL[row.source] || row.source || "—"}
                    </span>
                    <span className="ml-auto text-gray-500">{when(row.at)}</span>
                  </div>
                  <div className="mt-0.5 text-gray-800">{row.summary || "no scope changes"}</div>
                  <div className="mt-0.5 text-gray-500">
                    {row.actor}
                    {row.note ? ` · “${row.note}”` : ""}
                    {row.reason ? ` · reason: ${row.reason}` : ""}
                    {row.related_co_id ? ` · CO ${row.related_co_id}` : ""}
                    {!isVersion && row.entry_count ? ` · ${row.entry_count} cell change(s)` : ""}
                  </div>
                  {isVersion && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              onClick={() => onView({ id: row.id, label: `v${row.version_no}`, mode: "view" })}
                              data-testid={`frs-view-version-${row.id}`}>
                        View this version
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              onClick={() => onView({ id: row.id, label: `v${row.version_no}`, mode: "compare" })}
                              data-testid={`frs-compare-version-${row.id}`}>
                        <ArrowLeftRight className="w-3 h-3 mr-1" /> Compare to Current
                      </Button>
                      {row.id !== data.current_version_id && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-amber-800"
                                disabled={busy === row.id} onClick={() => restore(row)}
                                data-testid={`frs-restore-version-${row.id}`}>
                          {busy === row.id
                            ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            : <RotateCcw className="w-3 h-3 mr-1" />}
                          Restore
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
    </>
  );

  if (inline) return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid="frs-history-inline">
      <div className="px-3 py-2 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">FRS edit history</h3>
        <p className="text-[11px] text-gray-500">Grid saves and versions, newest first. View or compare opens the FRS read-only.</p>
      </div>
      <div className="max-h-[480px] overflow-y-auto flex flex-col">{body}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[150] flex justify-end bg-black/30" data-testid="frs-history-drawer">
      <button type="button" aria-label="Close" className="flex-1" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full flex flex-col shadow-xl">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">FRS history</h3>
          {viewing && (
            <Button size="sm" variant="outline" className="h-7 text-xs ml-auto"
                    onClick={() => onView(null)} data-testid="frs-back-to-current">
              Back to Current
            </Button>
          )}
          <button type="button" onClick={onClose} className={`h-7 w-7 grid place-items-center ${viewing ? "" : "ml-auto"}`}
                  data-testid="frs-history-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}
